import type { NoteCategory } from 'api-validations/scouting'

/**
 * Per-mode transient UI state. The spec models each mode as having its own
 * Session entity whose lifetime is tied to the active Stream — survives mode
 * changes, resets on stream change. Today these states live in component-local
 * `useState`/`useReducer`, so they die on every remount; lifting them here
 * gives them the spec's lifetime semantics.
 *
 * `current_record` itself stays at the top of MapState (it's shared across
 * modes, not session-scoped). The `recordSelections` slot below carries only
 * the back/forward jump-history stacks; the "current" position lives in
 * `MapState.record`.
 */

/**
 * Back/forward jump history shared between Diagnostics and Config. The
 * "current" record is `MapState.record`; this slot only stores the stacks.
 *
 * Scrubber drag, map click, and DBM-navigation push onto `previous` (the back
 * stack); `goBack`/`goForward` shuttle entries between the two without
 * recording new history. Per-spec, First/Last and Next/Prev do NOT push.
 */
export type RecordHistory = {
  previous: number[]
  next: number[]
}

export const emptyRecordHistory: RecordHistory = { previous: [], next: [] }

export type DiagnosticsSession = {
  /**
   * Module ids whose "show healthy locations" toggle is on. Empty by default;
   * each entry persists across mode toggles until the stream changes.
   */
  expandedModules: Set<string>

  /**
   * The currently-open ConfigPopover. Anchored to a specific (Module, row)
   * pair per spec — opening on a different row replaces the previous popover
   * even if the module is the same. `null` when no popover is open.
   */
  configPopover: ConfigPopoverAnchor | null

  /**
   * Module keys named by an event code the user jumped to from the map. Each
   * matching table scrolls itself into view and the consumer clears the field,
   * so the same code can be jumped to again. `null` when no jump is pending.
   */
  focusedModules: readonly string[] | null
}

export type ConfigPopoverAnchor = {
  moduleId: string
  /** Identifies which row in the module table the cog was clicked from. Matches the Table row `key` type (`string | number`). */
  rowKey: string | number
}

export const emptyDiagnosticsSession: DiagnosticsSession = {
  expandedModules: new Set(),
  configPopover: null,
  focusedModules: null
}

export type ConfigSession = {
  /**
   * Keys of multi-value setting accordions that are expanded in the Config
   * pane. Composite key: `${moduleKey}|${settingLabel}` — uniquely identifies
   * one accordion across the rendered settings tree.
   */
  expandedMultiSettings: Set<string>
}

export const emptyConfigSession: ConfigSession = {
  expandedMultiSettings: new Set()
}

export type ScoutingSession = {
  /**
   * Active filter categories on the notes list. Empty array means "show all".
   * Filter values are the same `NoteCategory` strings the backend uses; the
   * UI maps user-facing pill labels to underlying categories at the call site.
   */
  filters: NoteCategory[]

  /**
   * The currently-focused note id, or `null`. `null` is distinct from
   * "unknown" — when set, it overrides the default-active-note resolution
   * inside `useScoutingNotesLayer`.
   */
  activeNoteId: string | null

  /**
   * The note under the cursor and where the cursor is — over its map marker or
   * its card in the notes list. The list scrolls a map-hovered note into view;
   * a list hover means the cursor is already on it. `null` when no note is
   * hovered.
   */
  hoverNote: { noteId: string; source: ScoutingHoverSource } | null

  /** Whether the active note's description is expanded in the details panel. */
  descriptionExpanded: boolean
}

export const emptyScoutingSession: ScoutingSession = {
  filters: [],
  activeNoteId: null,
  hoverNote: null,
  descriptionExpanded: false
}

export enum ScoutingHoverSource {
  Map = 'Map',
  List = 'List'
}

/**
 * One kept output range for the stream trim/split editing map layer.
 */
export type StreamEditRange = { first: number; last: number; color: string }

/**
 * Active stream trim/split editing session: the stream being edited and the ranges to keep.
 */
export type StreamEditSession = {
  streamId: string
  ranges: StreamEditRange[]
  /**
   * Shown on the map for any record outside every kept range.
   */
  trimmedColor: string
}
